import type { FullNameEmployee } from '@/types/employee';
import type { DetailedExecutor, Executor } from '@/types/executor';
import type { Module } from '@/types/module';
import type { Mutation } from '@/types/mutation';
import type { NameIdPair } from '@/types/nameIdPair';
import type { Note } from '@/types/note';
import type { Organization } from '@/types/organization';
import type { Priority } from '@/types/priority';
import type { DetailedResponsible, Responsible } from '@/types/responsible';
import type { Risk } from '@/types/risk';
import type { Source } from '@/types/source';

/**
 * Needed for creating a new ActionPoint since the _id is generated by the server.
 */
export type ActionPointWithoutId = {
    number?: number;
    description?: string;
    module: Module['_id'];
    deadline?: Date | null;
    action: string;
    responsible?: Responsible | null;
    executor?: Executor | null;
    status: ActionPointStatus;
    completedOn?: Date;
    organization: Organization['_id'];
    risk: Risk['_id'];
    priority?: Priority['_id'];
    source?: Source['_id'] | null;
    notes?: Note[] | null;
};

/**
 * This is the shortened ActionPoint type. where the objects inside are NOT populated.
 */
export type ActionPoint = { _id: string } & ActionPointWithoutId & Mutation;

/**
 * ActionPoint with populated fields.
 */
export type DetailedActionPoint = {
    _id: string;
    number?: number;
    description?: string;
    module: Module;
    deadline?: Date | null;
    action: string;
    status: ActionPointStatus;
    organization: NameIdPair;
    createdBy: FullNameEmployee;
    risk: Risk;
    priority: Priority;
    createdOn: Date | null;
    updatedBy: FullNameEmployee;
    updatedOn: Date | null;
    updatedMessage: string;
    responsible?: DetailedResponsible | null;
    executor?: DetailedExecutor | null;
    source: Source | null;
    notes: NoteCreatedByFullName[] | null;
    completedOn: ActionPoint['completedOn'];
};

export type NoteCreatedByFullName = Note & { createdBy: FullNameEmployee };

/** Type for the create and edit form of ActionPoints. All properties are nullable so we can easily init a ref of this type with an empty object */
export type ActionPointForm = {
    action?: string;
    description?: string;
    module?: string;
    deadline?: string | null;
    responsible?: DetailedResponsible | null;
    executor?: DetailedExecutor | null;
    risk?: string;
    priority?: string;
    source?: string;
    status: ActionPointStatus;
    organization?: string;
};

type actionPointStatusses = ['notStarted', 'started', 'inProgress', 'done'];
export type ActionPointStatus = actionPointStatusses[number];

export const actionPointStatusTypeItems: ActionPointStatusTypeItem[] = [
    { title: 'Niet gestart', value: 'notStarted', color: 'red' },
    { title: 'Gestart', value: 'started', color: 'primary-dark' },
    { title: 'In behandeling', value: 'inProgress', color: 'primary-dark' },
    { title: 'Afgerond', value: 'done', color: 'green' }
] as const;

export type ActionPointStatusTypeItem = {
    title: string;
    value: ActionPointStatus;
    color: string;
};

export const actionPointSubjects = [
    'Arbozorg en verzuimbeleid',
    'Noodvoorzieningen, organisatie en BHV',
    'Inrichting arbeidsplaatsen',
    'Arbeidsmiddelen',
    'Gevaarlijke stoffen en biologische agentia',
    'Fysieke belasting',
    'Beeldschermwerk',
    'Fysische factoren',
    "PBM's, veiligheids- en gezondheidssingalering",
    'Aanvullende informatie Arbocatalogus',
    'Werk- en rusttijden',
    'RI&E- Plan van Aanpak'
];

export type ImportOptions = {
    headers: ImportOptionHeaders;
    workSheetName?: string;
    uploadFileToDocuments: boolean;
    shouldImportActionPoints: boolean;
};

export type ImportOptionHeaders = {
    subject: string;
    action: string;
    risk: string;
    source: string;
    responsible: string;
    aimDate: string;
    info: string;
    comment: string;
};

export const translatedImportOptionHeaders = {
    subject: 'Onderwerp',
    action: 'Actie',
    risk: 'Risiconiveau',
    source: 'Bron',
    responsible: 'Verantwoordelijk',
    aimDate: 'Streefdatum gereed',
    info: 'Informatie',
    comment: 'Opmerking'
};

export const defaultImportOptions: ImportOptions = {
    headers: {
        subject: 'Hoofdstuk/tabblad',
        action: 'Te nemen actie',
        risk: 'Risico-klasse',
        source: 'Bron',
        responsible: 'Verantwoordelijk',
        aimDate: 'Streefdatum gereed',
        info: 'Informatie',
        comment: 'Opmerking'
    },
    workSheetName: 'Plan van Aanpak',
    uploadFileToDocuments: false,
    shouldImportActionPoints: true
};
